import React, { useLayoutEffect, useState } from "react";
import { useSelector } from "react-redux";
import { getDashboardData } from "../../../../../utils/dashboard";
import common from "../../../../../utils/common";
import { formatCurrency } from "../../../../../utils/stringUtils";

import {
  Backdrop,
  Button,
  Chart,
  ChartNew,
  Chip,
  DevNote,
  ExpandableWrapper,
  InfoCardNewAdv,
  InfoTimeCardAdv,
  InfoWrapper,
  LogoLoader,
  TableAdv,
} from "../../../../../components";
import { fetchTimesheetsPlacements } from "../../../../../redux/slices/timesheets/timesheetApi";
import { exactUTCDate } from "../../../../../utils/dateFormator";
import dayjs from "dayjs";
import { ENDPOINTS } from "../../../../../redux/apiConfig";
import axiosRequest from "../../../../../redux/slices/universal/axiosService";
import KpiViewModal from "../../../Dashboard/Modals/KpiViewModal";
import { mapTableRows } from "../../../../../utils";

const pendingPayoutColumns = [
  { key: "clientName", label: "Client Name" },
  { key: "payPeriod", label: "Pay Period" },
  { key: "totalHours", label: "Total Hours" },
  { key: "usPayRate", label: "US Pay Rate" },
  { key: "inrPayRate", label: "INR Pay Rate" },
  { key: "balance", label: "Balance", total: true },
];

const adjustmentsColumns = [
  { key: "clientName", label: "Client Name" },
  { key: "period", label: "Period" },
  { key: "orgPay", label: "Original Pay" },
  { key: "revPay", label: "Revised Pay" },
  { key: "diff", label: "Difference", total: true },
  { key: "status", label: "Status" },
];

const deductionColumns = [
  { key: "deductionName", label: "Deduction Name" },
  { key: "total", label: "Total Amount" },
  { key: "recovered", label: "Recovered" },
  { key: "balance", label: "Balance", total: true },
  { key: "payDate", label: "Pay Date" },
  { key: "installments", label: "Installments" },
];

const healthInsuranceColumns = [
  { key: "insuranceType", label: "Insurance Type" },
  { key: "amount", label: "Amount", total: true },
];

const PaymentsDashboard = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [kpiModalOpen, setKpiModalOpen] = useState(false);
  const [dashboardCardsData, setDashboardCardsData] = useState([
    {
      title: "Pending Payout",
      value: "0",
      prefix: "$",
    },
    {
      title: "Deduction",
      value: "0",
      prefix: "$",
    },
    {
      title: "Health Insurance",
      value: "0",
      prefix: "$",
    },
    {
      title: "Adjustments",
      value: "0",
      prefix: "$",
    },
    {
      title: "Total Balance",
      value: "0",
      prefix: "$",
    },
  ]);
  const [pendingPayout, setPendingPayout] = useState([]);
  const [adjustments, setAdjustments] = useState({
    short: [],
    excess: [],
    shortDiff: 0,
    excessDiff: 0,
  });
  const [deductions, setDeductions] = useState({
    legalDeductions: [],
    healthInsurance: [],
  });
  const [totals, setTotals] = useState({
    pendingPayout: 0,
    deductions: 0,
  });
  const [dateRanges, setDateRanges] = useState({
    pendingPayout: {
      from: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000),
      to: new Date(),
    },
  });
  const { getCutoffEndDateWithDayjs } = common;
  const { employeeProfileUiid } = useSelector((state) => state.employee);

  // /fetch dashboard card calls
  const fetchDashboardCards = async () => {
    const dashboard = await getDashboardData(
      {
        empPaydata: {
          type: "AG_STATS",
          value: [
            employeeProfileUiid,
            getCutoffEndDateWithDayjs(undefined, "YYYY-MM-DD"),
          ],
        },
      },
      "Employee",
    );
    let cardsResponse = dashboard?.[0] || {};
    setDashboardCardsData([
      {
        title: "Pending Payout",
        value: formatCurrency(cardsResponse?.pendingPayout),
        // prefix: "$",
      },
      {
        title: "Deduction",
        value: formatCurrency(cardsResponse?.deductions),
        // prefix: "$",
      },
      {
        title: "Health Insurance",
        value: formatCurrency(cardsResponse?.health),
        // prefix: "$",
      },
      {
        title: "Adjustments",
        value:
          (Math.abs(cardsResponse?.adjustments?.excessDiff) >
          cardsResponse?.adjustments?.excessDiff
            ? "-"
            : "") + formatCurrency(cardsResponse?.adjustments?.diff),
        // prefix: "$",
      },
      {
        title: "Total Balance",
        value: formatCurrency(cardsResponse?.totalBal),
        // prefix: "$",
      },
    ]);
  };
  //fetch unpaid timehseets and
  const fetchTimesheetData = async () => {
    const payload = {
      limit: 1000,
      page: 1,
      useAggregate: true,
      filters: {
        pendingPay: {
          type: "AG_LOOKUP_DATA",
          value: [
            employeeProfileUiid,
            6,
            false,
            dayjs(dateRanges?.pendingPayout?.from).format("YYYY-MM-DD"),
            dayjs(
              dateRanges?.pendingPayout?.to || getCutoffEndDateWithDayjs(),
            ).format("YYYY-MM-DD"),
          ],
        },
      },
      ref: "Client Placement",
      incl: ["businessName", "jobInfo.jobTitle payments"],
    };
    const res = await fetchTimesheetsPlacements(payload);
    const unpaidTs = res?.data?.data || [];
    const mappedPendingPayout = unpaidTs?.map((item) => {
      const lastPayment =
        item?.placementPayments?.[item?.placementPayments?.length - 1];
      // for US payrate
      const payRatePerHour = lastPayment?.payRate?.payRatePerHour || 0;
      const subPercent = lastPayment?.payRate?.subPercent || 0;
      const rate =
        subPercent > 0 ? (payRatePerHour * 100) / subPercent : payRatePerHour;
      // for INR payrate
      const inrRate = item?.placementPayments?.[
        item?.placementPayments?.length - 1
      ]?.payRate?.subPercent
        ? item?.placementPayments?.[item?.placementPayments?.length - 1]
            ?.payRate?.payRatePerHour
        : "-" || 0;
      // to calculate total hours for each Timesheets
      const calculatedTotalHours = item?.dailyEntries?.reduce(
        (acc, sum) => acc + sum.st,
        0,
      );
      return {
        "Client Name": item?.clientName || "",
        "Pay Period":
          exactUTCDate(item?.period?.from) +
          "-" +
          exactUTCDate(item?.period?.to),
        "Total Hours": Number(calculatedTotalHours).toFixed(2),
        "US Pay Rate": Number(rate).toFixed(2),
        "INR Pay Rate": inrRate,
        Balance:
          // formatCurrency(item?.amounts?.total - item?.amounts?.paid) || 0,
          Number(item?.amounts?.total - item?.amounts?.paid).toFixed(2) || 0,
      };
    });
    let totalPendingPayout = unpaidTs
      ?.flatMap((a) => (a?.amounts?.total || 0) - (a?.amounts?.paid || 0))
      ?.reduce((acc, sum) => acc + sum, 0);
    setPendingPayout(mappedPendingPayout);
    setTotals((prev) => ({
      ...prev,
      pendingPayout: totalPendingPayout,
    }));
  };
  //fetch adjustments
  const fetchAdjustments = async () => {
    try {
      const [adjustments] = await Promise.allSettled([
        axiosRequest({
          method: "GET",
          url: ENDPOINTS.EMPLOYEE_ADJUSTMENTS.GET_ALL,
          params: {
            coll: "EmpAdjustment",
            ref: "Placement Client",
            ret: "type clientId period pay usage status",
            incl: ["jobInfo", "businessName"],
            lim: 1000,
            pg: 1,
            fil: JSON.stringify({
              employeeId: { type: "NONE", value: employeeProfileUiid },
              // type: { type: "NONE", value: 1 },
            }),
          },
        }),
      ]);
      const short = [];
      const excess = [];
      const allAdjustments = adjustments?.value?.data?.data;
      allAdjustments?.forEach((adjustment) => {
        if (adjustment.type === 1) {
          short.push(adjustment);
        } else if (adjustment.type === 2) {
          excess.push(adjustment);
        }
      });
      setAdjustments({
        short,
        excess,
      });
    } catch (error) {
      console.error("error in adjustments", error);
    }
  };
  //fetch deductions
  const fetchDeductions = async () => {
    try {
      const [allDeductions, allHealthInsurances] = await Promise.allSettled([
        axiosRequest({
          method: "GET",
          url: ENDPOINTS.EMPLOYEE_DEDUCTIONS.GET_ALL,
          params: {
            coll: "EmpDeduction",
            lim: 1000,
            pg: 1,
            ret: "name amounts installments payDate",
            fil: JSON.stringify({
              deductionType: { type: "NONE", value: 1 },
              stop: { type: "NONE", value: false },
              employeeId: { type: "NONE", value: employeeProfileUiid },
            }),
          },
        }),
        axiosRequest({
          method: "GET",
          url: ENDPOINTS.EMPLOYEE_DEDUCTIONS.GET_ALL,
          params: {
            coll: "EmpDeduction",
            lim: 1000,
            pg: 1,
            fil: JSON.stringify({
              deductionType: { type: "NONE", value: 2 },
              stop: { type: "NONE", value: false },
              employeeId: { type: "NONE", value: employeeProfileUiid },
            }),
          },
        }),
      ]);
      setDeductions({
        legalDeductions: allDeductions?.value?.data?.data || [],
        healthInsurance: allHealthInsurances?.value?.data?.data || [],
      });
      const totalLegaldeductions = allDeductions?.value?.data?.data?.reduce(
        (acc, sum) => acc + (sum.amounts?.deducted - sum.amounts?.received),
        0,
      );
      setTotals((prev) => ({
        ...prev,
        deductions: totalLegaldeductions,
      }));
    } catch (error) {
      console.error("error in deductions", error);
    }
  };

  //calls to fetch all dashboards apis
  const handleDashboardCalls = async () => {
    const startTime = performance.now();
    try {
      setIsLoading(true);
      await Promise.allSettled([
        fetchDashboardCards(),
        fetchTimesheetData(),
        fetchAdjustments(),
        fetchDeductions(),
      ]);
      const endTime = performance.now();
      console.assert(
        import.meta.env.VITE_APP_ENV !== "local",
        `Time complexity: ${Number(endTime - startTime).toFixed(2)}ms`,
      );
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };
  useLayoutEffect(() => {
    handleDashboardCalls();
  }, [dateRanges]);

  // helper functions to transform state into table rows for modal
  const transformPendingRows = () =>
    pendingPayout.map((r, idx) => ({
      id: idx + 1,
      clientName: r["Client Name"] || "",
      payPeriod: r["Pay Period"] || "",
      totalHours: r["Total Hours"] || "",
      usPayRate: r["US Pay Rate"] || "",
      inrPayRate: r["INR Pay Rate"] || "",
      balance: r.Balance || 0,
    }));

  const transformDeductionsRows = () =>
    deductions.legalDeductions.map((d, idx) => ({
      id: idx + 1,
      deductionName: d?.name || "",
      total: formatCurrency(d?.amounts?.deducted) || "",
      recovered: formatCurrency(d?.amounts?.received) || "",
      balance: (d?.amounts?.deducted || 0) - (d?.amounts?.received || 0),
      payDate: dayjs(d?.payDate).format("MM/DD/YYYY") || "",
      installments:
        `${
          d?.installments.filter(
            (data) => !data.receivable || data.receivable === 0,
          ).length
        }/${d?.installments?.length}` || "",
    }));

  const transformHealthRows = () =>
    deductions.healthInsurance.map((d, idx) => ({
      id: idx + 1,
      insuranceType: d?.name || "",
      amount: d?.amounts?.deducted || 0,
    }));

  const transformAdjustmentRows = (type) => {
    const arr = type === "short" ? adjustments.short : adjustments.excess;
    return arr.map((row, idx) => ({
      id: idx + 1,
      clientName: row.clientId?.businessName || "",
      period:
        exactUTCDate(row.period?.from) + "-" + exactUTCDate(row.period?.to),
      orgPay: formatCurrency(row?.pay?.original),
      revPay: formatCurrency(row?.pay?.revised),
      diff: Number(row?.pay?.original - row?.pay?.revised)?.toFixed(2),
      status: row?.status === 1 ? "Active" : "Inactive",
    }));
  };

  const getTotalBalanceRows = () =>
    dashboardCardsData.map((item, idx) => ({
      id: idx + 1,
      category: item.title,
      amount: parseFloat(item.value.replace(/[^0-9.-]+/g, "")) || 0,
    }));

  const renderKpiModalContent = () => {
    switch (kpiModalOpen) {
      case "Pending Payout":
        return (
          <TableAdv
            tableId={`employee_payments_paydatadashboardkpi_${kpiModalOpen}`}
            showSlNo
            columns={pendingPayoutColumns}
            rows={mapTableRows(transformPendingRows(), pendingPayoutColumns)}
            totalCount={transformPendingRows().length}
          />
        );
      case "Deduction":
        return (
          <TableAdv
            tableId={`employee_payments_paydatadashboardkpi_${kpiModalOpen}`}
            showSlNo
            columns={deductionColumns}
            rows={mapTableRows(transformDeductionsRows(), deductionColumns)}
            totalCount={transformDeductionsRows().length}
          />
        );
      case "Health Insurance":
        return (
          <TableAdv
            tableId={`employee_payments_paydatadashboardkpi_${kpiModalOpen}`}
            showSlNo
            columns={healthInsuranceColumns}
            rows={mapTableRows(transformHealthRows(), healthInsuranceColumns)}
            totalCount={transformHealthRows().length}
          />
        );
      case "Adjustments":
        return (
          <>
            <h5 className="mt-2">Short</h5>
            <TableAdv
              tableId={`employee_payments_paydatadashboardkpi_short`}
              showSlNo
              columns={adjustmentsColumns}
              rows={mapTableRows(
                transformAdjustmentRows("short"),
                adjustmentsColumns,
              )}
              totalCount={transformAdjustmentRows("short").length}
            />
            <h5 className="mt-4">Excess</h5>
            <TableAdv
              tableId={`employee_payments_paydatadashboardkpi_excess`}
              showSlNo
              columns={adjustmentsColumns}
              rows={mapTableRows(
                transformAdjustmentRows("excess"),
                adjustmentsColumns,
              )}
              totalCount={transformAdjustmentRows("excess").length}
            />
          </>
        );
      case "Total Balance":
        return (
          <TableAdv
            tableId={`employee_payments_paydatadashboardkpi_${kpiModalOpen}`}
            // showSlNo
            columns={[
              {
                key: "category",
                label: "Category",
                getRowClass: (row) => {
                  return row?.category === "Total Balance" ? "bg-gray-100 hover:bg-gray-200 font-semibold" : "";
                },
              },
              {
                key: "amount",
                label: "Amount",
                render: (row) => formatCurrency(row?.amount),
              },
            ]}
            rows={mapTableRows(getTotalBalanceRows(), [
              { key: "category" },
              { key: "amount" },
            ])}
            totalCount={getTotalBalanceRows().length}
          />
        );
      default:
        return null;
    }
  };

  if (kpiModalOpen) {
    return (
      <div className="mt-4 border-t border-gray-200 pt-4">
        <div className="flex items-center gap-2 mb-3">
          <Button
            size="md"
            title={"Back"}
            variant="text"
            icon={"arrow_left_alt"}
            color="black"
            className="px-0! bg-transparent!"
            onClick={() => setKpiModalOpen(false)}
          />
        </div>
        {/* show selected card title */}
        <h4 className="mb-2 text-lg font-semibold capitalize">
          {kpiModalOpen}
        </h4>
        {renderKpiModalContent()}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-4 px-1 py-2">
      {/* <DevNote> */}
      <>
        {import.meta.env.VITE_APP_ENV === "local" && (
          <div className="flex items-center justify-end col-span-2 w-max-[50px]">
            <Button
              onClick={() => {
                handleDashboardCalls();
              }}
              variant="outlined"
              icon="autorenew"
              title={"Refresh"}
            />
          </div>
        )}
      </>
      {/* </DevNote> */}
      <div className="bg-[#FAFCFF] col-span-2 flex flex-col px-3 py-4 border border-gray-200 rounded-2xl shadow-lg">
        <h5>Dashboard</h5>
        {/* <InfoWrapper> */}
        <div className="grid grid-cols-5 gap-4">
          {dashboardCardsData?.map((item, _idx) => (
            <InfoCardNewAdv
              key={_idx}
              title={item?.title}
              value={item?.value}
              prefix={item?.prefix || ""}
              suffix={item?.suffix || ""}
              layout="option9"
              className="cursor-pointer"
              titleConfig={{ className: "text-[16px]!" }}
              onClick={() => setKpiModalOpen(item?.title)}
              isLoading={isLoading}
            />
          ))}
        </div>
        {/* </InfoWrapper> */}
        {/* </div> */}
      </div>
      <div className="col-span-2 grid lg:grid-cols-2 gap-4">
        <div className="bg-[#FAFCFF] flex flex-col h-[400px] px-3 py-4 border border-gray-200 rounded-2xl shadow-lg">
          <ChartNew
            title="Pending Payout"
            datepicker={true}
            dateValue={{
              from: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000),
              to: new Date(),
            }}
            expandIcon={false}
            onDateChange={(value) =>
              setDateRanges((prev) => ({
                ...prev,
                pendingPayout: value,
              }))
            }
            type="tablechart"
            rawData={pendingPayout}
            tableChartProps={{
              tableTotalKey: "Balance",
            }}
          />

          {/* <div className="flex justify-end items-center text-white gap-1 mt-1">
          <div className="bg-primary w-[200px] py-3 px-4 rounded-l-lg">
            Total
          </div>
          <div className="bg-primary w-[200px] py-3 px-4 rounded-r-lg">
            {formatCurrency(totals.pendingPayout)}
          </div>
        </div> */}
        </div>

        <div className="bg-[#FAFCFF] h-[400px] flex flex-col px-3 py-4 border border-gray-200 rounded-2xl shadow-lg">
          <h5 className="text-lg mb-3">Adjustments</h5>
          <ChartNew
            type="tablechart"
            title="Short"
            height="h-fit"
            columns={adjustmentsColumns}
            tableChartProps={{
              tableTotalKey: "Difference",
              noImage: true,
            }}
            rawData={
              adjustments?.short?.map((row) => ({
                "Client Name": row.clientId?.businessName || "",
                Period:
                  exactUTCDate(row.period?.from) +
                  "-" +
                  exactUTCDate(row.period?.to),
                "Original Pay": formatCurrency(row?.pay?.original),
                "Revised Pay": formatCurrency(row?.pay?.revised),
                // "Difference": formatCurrency(row?.pay?.original - row?.pay?.revised),
                Difference: Number(
                  row?.pay?.original - row?.pay?.revised,
                )?.toFixed(2),
                Status: (
                  <Chip
                    size="sm"
                    // label={row?.usage?.payrollId ? "Paid" : "Pending"}
                    // color={row?.usage?.payrollId ? "success" : "danger"}
                    // className={`${
                    //   row?.usage?.payrollId ? "text-success" : "text-danger"
                    // } font-semibold rounded-xl px-3 py-1`}
                    //will update this block status once further info is recived
                    label={false ? "Paid" : "Pending"}
                    color={false ? "success" : "danger"}
                    className={`${
                      false ? "text-success" : "text-danger"
                    } font-semibold rounded-xl px-3 py-1`}
                  />
                ),
              })) || []
            }
          />
          <ChartNew
            type="tablechart"
            title="Excess"
            // height="h-fit"
            columns={adjustmentsColumns}
            tableChartProps={{
              tableTotalKey: "Difference",
              noImage: true,
            }}
            rawData={
              adjustments?.excess?.map((row) => ({
                "Client Name": row.clientId?.businessName || "",
                Period:
                  exactUTCDate(row.period?.from) +
                  "-" +
                  exactUTCDate(row.period?.to),
                "Original Pay": formatCurrency(row?.pay?.original),
                "Revised Pay": formatCurrency(row?.pay?.revised),
                // "Difference": formatCurrency(row?.pay?.original - row?.pay?.revised),
                Difference: Number(
                  row?.pay?.original - row?.pay?.revised,
                )?.toFixed(2),
                Status: (
                  <Chip
                    size="sm"
                    // label={row?.usage?.payrollId ? "Paid" : "Pending"}
                    // color={row?.usage?.payrollId ? "success" : "danger"}
                    // className={`${
                    //   row?.usage?.payrollId ? "text-success" : "text-danger"
                    // } font-semibold rounded-xl px-3 py-1`}
                    //will update this block status once further info is recived
                    label={false ? "Paid" : "Pending"}
                    color={false ? "success" : "danger"}
                    className={`${
                      false ? "text-success" : "text-danger"
                    } font-semibold rounded-xl px-3 py-1`}
                  />
                ),
              })) || []
            }
          />
        </div>

        <div className="bg-[#FAFCFF] flex flex-col items-stretch h-[400px] px-3 py-4 border border-gray-200 rounded-2xl shadow-lg overflow-hidden">
          <ChartNew
            type="tablechart"
            title="Deduction"
            // chartToolbarRight={{
            //   chartToggle: true,
            // }}
            tableChartProps={{
              tableTotalKey: "Balance",
            }}
            rawData={
              deductions?.legalDeductions?.map((d) => ({
                "Deduction Name": d?.name || "",
                "Total Amount": formatCurrency(d?.amounts?.deducted) || "",
                Recovered: formatCurrency(d?.amounts?.received) || "",
                // Balance:
                //   formatCurrency(d?.amounts?.deducted - d?.amounts?.received) ||
                //   "",
                Balance:
                  (d?.amounts?.deducted || 0) - (d?.amounts?.received || 0),
                "Pay Date": dayjs(d?.payDate).format("MM/DD/YYYY") || "",
                Installments:
                  `${
                    d?.installments.filter(
                      (data) => !data.receivable || data.receivable === 0,
                    ).length
                  }/${d?.installments?.length}` || "",
              })) || []
            }
          />

          {/* <div className="flex justify-end items-center text-white gap-1 mt-1">
          <div className="bg-primary w-[200px] py-3 px-4 rounded-l-lg">
            Total
          </div>
          <div className="bg-primary w-[200px] py-3 px-4 rounded-r-lg">
            {formatCurrency(totals?.deductions)}
          </div>
        </div> */}
        </div>

        <div className="bg-[#FAFCFF] flex flex-col h-[400px] px-3 py-4 border border-gray-200 rounded-2xl shadow-lg">
          {/* <ExpandableWrapper
          title="Health Insurance"
          datepicker={false}
          expandIcon={false}
          onDateChange={(value) => console.log("value: ", value)}
        > */}
          <ChartNew
            type="tablechart"
            title="Health Insurance"
            // onDateChange={(value) => console.log("value: ", value)}
            tableChartProps={{
              tableTotalKey: "Amount",
            }}
            rawData={
              deductions?.healthInsurance?.map((d) => ({
                "Insurance Type": d?.name || "",
                // Amount: formatCurrency(d?.amounts?.deducted),
                Amount: d?.amounts?.deducted || 0,
              })) || []
            }
            columns={healthInsuranceColumns}
          />
          {/* </ExpandableWrapper> */}
        </div>
      </div>
      <Backdrop isOpen={isLoading}>
        <LogoLoader />
      </Backdrop>
    </div>
  );
};

export default PaymentsDashboard;
